import React, { useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { imgUrl } from "@/lib/apiClient";

export const BundleBuilder = ({ product, flavors, open, onOpenChange }) => {
  const { addItem } = useCart();
  const [selected, setSelected] = useState([]);

  const total = product?.count || 0;
  const remaining = total - selected.length;

  const reset = () => setSelected([]);

  const pick = (flavor) => {
    if (selected.length >= total) return;
    setSelected((prev) => [...prev, flavor]);
  };

  const unpickAt = (idx) => {
    setSelected((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAdd = () => {
    addItem({
      product_type: product.type,
      label: product.label,
      price: product.price,
      flavors: selected,
    });
    toast.success(`${product.label} added to your box!`);
    reset();
    onOpenChange(false);
  };

  const slots = useMemo(() => Array.from({ length: total }), [total]);

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-cream" data-testid="bundle-builder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-ink">
            Build your {product.label}
          </DialogTitle>
          <p className="text-ashydark">
            Pick {total} cookies — mix & match any flavors. ${product.price.toFixed(2)} total.
          </p>
        </DialogHeader>

        {/* Sticky summary */}
        <div className="sticky top-0 z-10 -mx-6 border-y border-ashy/30 bg-babybluelight/90 px-6 py-3 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-ink">
              {remaining > 0 ? `${remaining} cookie${remaining > 1 ? "s" : ""} left to pick` : "Box complete!"}
            </span>
            <span className="text-sm font-semibold text-cocoa">
              {selected.length}/{total}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {slots.map((_, idx) => {
              const chosen = selected[idx];
              return (
                <div
                  key={idx}
                  onClick={() => chosen && unpickAt(idx)}
                  className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs transition-colors ${
                    chosen
                      ? "cursor-pointer bg-cocoa text-white hover:bg-destructive"
                      : "border border-dashed border-ashy text-ashydark"
                  }`}
                  data-testid="bundle-slot"
                >
                  {chosen ? (
                    <>
                      {chosen.name} <X className="h-3 w-3" />
                    </>
                  ) : (
                    `Slot ${idx + 1}`
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Flavor grid */}
        <div className="grid grid-cols-2 gap-3 py-4 sm:grid-cols-3">
          {flavors.map((flavor) => {
            const countPicked = selected.filter((s) => s.id === flavor.id).length;
            return (
              <button
                key={flavor.id}
                data-testid={`bundle-pick-${flavor.id}`}
                onClick={() => pick(flavor)}
                disabled={remaining === 0}
                className="group relative overflow-hidden rounded-xl border border-ashy/40 bg-white text-left transition-transform duration-300 hover:-translate-y-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <div className="relative h-24 overflow-hidden">
                  <img
                    src={imgUrl(flavor.image)}
                    alt={flavor.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {countPicked > 0 && (
                    <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-cocoa text-xs font-bold text-white">
                      {countPicked}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm font-medium text-ink">{flavor.name}</span>
                  <Plus className="h-4 w-4 text-cocoa" />
                </div>
              </button>
            );
          })}
        </div>

        <Button
          data-testid="add-bundle-to-cart-button"
          onClick={handleAdd}
          disabled={remaining !== 0}
          className="w-full rounded-full bg-cocoa py-6 text-base text-white hover:bg-cocoadark disabled:opacity-40"
        >
          {remaining === 0 ? (
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4" /> Add to Box · ${product.price.toFixed(2)}
            </span>
          ) : (
            `Pick ${remaining} more`
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
